'use client';

import { useLocale, useTranslations } from 'next-intl';
import { LocalDateTime } from '@/components/ui/LocalDateTime';
import { useHydratedTimeZone } from '@/hooks/useHydratedTimeZone';
import { cn } from '@/lib/utils';
import { isWinnerlessRow } from '@/draw/core/matchOutcome';
import { useOutcomeLabel } from '@/components/match/matchOutcomeLabel';
import { formatScheduledTime, type PublicMatch } from '../../libs/scheduleStatus';
import { normalizeTimelineZone } from '../../libs/scheduleTimeline';
import { winnerSide } from '../../libs/gridLive';
import { formatScoreLine } from '@/components/match/formatScoreLine';
import { NAME_WRAP } from '@/app/[locale]/event/[eventId]/libs/nameBreaks';
import { NameText } from '@/app/[locale]/event/[eventId]/[slug]/components/NameText';

interface GridMatchCardProps {
  match: PublicMatch;
  onTap: (matchId: string) => void;
  /**
   * Match tile with textual status, winner and scores. Timeline cards fit their exact scheduled
   * duration: shorter cards use compact content, medium cards clamp each name to one line, and
   * roomy cards allow two. The full accessible label and detail sheet retain complete match data.
   * Timeline cards include their scheduled time range; default cards keep the original layout.
   */
  timeline?: { endIso: string; compact: boolean; roomy?: boolean };
}

/**
 * Match tile with textual status, winner and scores. Timeline cards show a scheduled time range
 * and fit their exact duration, clamping names as needed. Complete names, seeds and scores remain
 * in the accessible label and detail sheet. Default cards keep their original wrapping layout.
 */
export function GridMatchCard({ match, onTap, timeline }: GridMatchCardProps) {
  const t = useTranslations('PublicSchedule');
  const locale = useLocale();
  const hydratedZone = useHydratedTimeZone();
  const displayZone = normalizeTimelineZone(
    hydratedZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const outcomeLabel = useOutcomeLabel();
  const tMatches = useTranslations('Matches');
  const isLive = match.status === 'live';
  const isDone = match.status === 'completed';
  const winnerless = isWinnerlessRow({ outcome: match.outcome, is_walkover: match.isWalkover });
  const outcomeText = winnerless ? outcomeLabel(match.outcome, match.isWalkover) : null;
  const winner = winnerSide(match);

  const header = [match.division, match.code].filter(Boolean);
  const retired = match.outcome === 'retired' || !!match.retiredPlayerId;
  const points = formatScoreLine(
    (match.games ?? []).map((g) => ({ player1: g.p1, player2: g.p2 })),
    { retired, marker: tMatches('retiredShort') },
  );
  const scoreLine = outcomeText
    ? { main: outcomeText, points: null }
    : match.score
      ? { main: `${match.score.p1}-${match.score.p2}`, points: points || null }
      : null;

  const accessibleLabel = timeline
    ? [
        ...match.players.map((name, i) =>
          [match.seeds?.[i] ? `[${match.seeds[i]}]` : '', name, winner === i ? t('winner') : '']
            .filter(Boolean)
            .join(' '),
        ),
        match.division,
        match.code,
        match.stage,
        isLive ? t('tileLive') : isDone ? t('tileFinal') : '',
        scoreLine?.main,
        scoreLine?.points,
        t('timelineNote'),
        formatScheduledTime(match.scheduledAt, locale, displayZone),
        formatScheduledTime(timeline.endIso, locale, displayZone),
      ]
        .filter(Boolean)
        .join(' · ')
    : undefined;
  const times = timeline && (
    <span className="flex max-w-full min-w-0 shrink-0 items-center gap-1 font-mono text-[0.625rem] leading-tight tabular-nums">
      <LocalDateTime
        iso={match.scheduledAt}
        className="min-w-0 truncate"
        locale={locale}
        format={(iso, locale) => formatScheduledTime(iso, locale, displayZone)}
      />
      <span aria-hidden="true" className="@max-[11rem]/tile:hidden">
        –
      </span>
      <LocalDateTime
        iso={timeline.endIso}
        className="@max-[11rem]/tile:hidden"
        locale={locale}
        format={(iso, locale) => formatScheduledTime(iso, locale, displayZone)}
      />
    </span>
  );
  const color = cn(
    isDone && 'bg-success/10 ring-success/40',
    isLive && 'bg-accent/10 ring-accent/60',
    !isDone && !isLive && 'bg-surface ring-border',
  );
  if (timeline?.compact)
    return (
      <button
        type="button"
        onClick={() => onTap(match.id)}
        aria-label={accessibleLabel}
        title={accessibleLabel}
        data-status={match.status}
        className={cn(
          'text-foreground focus-visible:ring-ring flex h-full min-h-0 w-full cursor-pointer flex-col justify-center gap-px overflow-hidden rounded-lg p-0.5 text-left ring-1 ring-inset hover:brightness-[.97] focus-visible:ring-2 focus-visible:outline-none',
          color,
        )}
      >
        <span className="flex w-full min-w-0 items-center justify-between gap-1">
          {times}
          <span className="min-w-0 flex-1 truncate text-right text-[0.625rem] font-semibold @max-[5rem]/tile:hidden">
            <span className="@max-[11rem]/tile:hidden">
              {isLive ? t('tileLive') : isDone ? t('tileFinal') : null}
            </span>
            {scoreLine && <span className="ml-1 @max-[11rem]/tile:ml-0">{scoreLine.main}</span>}
          </span>
        </span>
        {match.players.map((name, i) => (
          <span
            key={i}
            data-side={i}
            className={cn(
              'block w-full shrink-0 truncate text-xs leading-none',
              winner === i ? 'font-bold' : 'font-medium',
            )}
          >
            {name}
          </span>
        ))}
      </button>
    );

  return (
    <button
      type="button"
      onClick={() => onTap(match.id)}
      data-status={match.status}
      aria-label={accessibleLabel}
      title={
        accessibleLabel ??
        [
          t('matchTitle', {
            home: match.players[0] ?? t('tbd'),
            away: match.players[1] ?? t('tbd'),
          }),
          match.stage,
          match.division,
        ]
          .filter(Boolean)
          .join(' · ')
      }
      className={cn(
        'text-foreground focus-visible:ring-ring flex min-h-11 w-full cursor-pointer flex-col gap-0.5 rounded-lg p-1.5 text-left leading-snug ring-1 transition-[filter] ring-inset hover:brightness-[.97] focus-visible:ring-2 focus-visible:outline-none',
        timeline && 'h-full min-h-0 overflow-hidden @max-[11rem]/tile:p-1',
        isDone && 'bg-success/10 ring-success/40',
        isLive && 'bg-accent/10 ring-accent/60',
        !isDone && !isLive && 'bg-surface ring-border',
      )}
    >
      {times}
      {(header.length > 0 || isLive || isDone) && (
        <span
          className={cn(
            'text-muted-foreground flex shrink-0 items-baseline justify-between gap-2 font-mono text-[0.6875rem] font-semibold tracking-[0.06em] uppercase',
            timeline &&
              'max-w-full min-w-0 @max-[11rem]/tile:flex-col @max-[11rem]/tile:items-stretch @max-[11rem]/tile:gap-0',
          )}
        >
          <span
            className={
              timeline
                ? 'max-w-full min-w-0 truncate @max-[5rem]/tile:hidden'
                : 'min-w-0 break-words'
            }
          >
            {header.join(' · ')}
          </span>
          {isLive && (
            <span
              className={cn(
                'text-foreground flex shrink-0 items-center gap-1',
                timeline && '@max-[11rem]/tile:min-w-0 @max-[11rem]/tile:truncate',
              )}
            >
              <span aria-hidden="true" className="bg-accent size-1.5 rounded-full" />
              {t('tileLive')}
            </span>
          )}
          {isDone && (
            <span
              className={cn(
                'text-success-emphasis shrink-0',
                timeline && 'max-w-full min-w-0 truncate',
              )}
            >
              {t('tileFinal')}
            </span>
          )}
        </span>
      )}
      {match.players.map((name, i) => (
        <span
          key={i}
          data-side={i}
          className={cn(
            timeline
              ? cn(
                  'w-full min-w-0 shrink-0 text-sm leading-tight',
                  NAME_WRAP,
                  timeline.roomy ? 'line-clamp-[1] @[11rem]/tile:line-clamp-[2]' : 'line-clamp-1',
                )
              : cn('block text-sm', NAME_WRAP),
            winner === i ? 'font-bold' : 'font-medium',
            winner !== null && winner !== i && 'text-muted-foreground',
          )}
        >
          {match.seeds?.[i] && (
            <>
              <span className="text-muted-foreground font-mono text-[0.6875rem] font-normal whitespace-nowrap">
                [{match.seeds[i]}]
              </span>{' '}
            </>
          )}
          <NameText name={name} />
          {winner === i && (
            <>
              {' '}
              <span aria-hidden="true" className="text-foreground font-semibold">
                ✓
              </span>
              <span className="sr-only"> {t('winner')}</span>
            </>
          )}
        </span>
      ))}
      {scoreLine && (
        <span
          className={cn(
            'font-mono text-sm font-semibold break-words tabular-nums',
            timeline && 'line-clamp-1 max-w-full min-w-0 shrink-0 leading-tight',
          )}
        >
          {scoreLine.main}
          {scoreLine.points && (
            <span
              className={cn(
                'text-muted-foreground ml-1 text-[0.6875rem] font-normal',
                timeline && '@max-[11rem]/tile:hidden',
              )}
            >
              ({scoreLine.points})
            </span>
          )}
        </span>
      )}
    </button>
  );
}
